import React, { useEffect, useState, useMemo } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "../../i18n/useTranslation";
import { useWizard } from "../../context/WizardContext";
import { useAuth } from "../../context/AuthContext";
import { useUiLocale } from "../../context/UiLocaleContext";
import { resolveCoverImageUrl, resolveStoryImageUrl } from "../../utils/presignedImageUrl";
import { coverDisplayTitleFromOverlays } from "../../utils/coverOverlayTitle";
import LogoLoader from "../common/LogoLoader";
import { API_BASE_URL } from "../../config/apiBaseUrl";
import AdminPageNavigator from "../AdminPageNavigator";
import type { BookPage } from "./bookPageModel";
import { useMediaQuery } from "../../utils/useMediaQuery";
import { coerceStoryPageNumber, findStoryPageSlotImage } from "../../utils/storyPageImageMatch";

interface PageImage {
  pageNumber: number;
  isSidePicture?: boolean;
  editedImageUrl?: string;
  uploadedImageUrl?: string;
  imageUrl?: string;
  editedImageStorageKey?: string;
  uploadedImageStorageKey?: string;
  imageStorageKey?: string;
  /** User-provided description for accessibility; used as img alt when set. */
  accessibleDescription?: string;
}

interface CoverData {
  editedImageUrl?: string;
  uploadedImageUrl?: string;
  imageUrl?: string;
  editedImageStorageKey?: string;
  uploadedImageStorageKey?: string;
  imageStorageKey?: string;
  textOverlays?: any[];
  accessibleDescription?: string;
}

interface StoryData {
  sessionId: string;
  language: string;
  status?: string;
  submissionStatus?: string;
  userChangeRequestText?: string;
  userChangeRequestedAt?: string | null;
  userApprovedAt?: string | null;
  /** ISO string from API — last time story pictures were emailed to the owner. */
  storyImagesLastSentAt?: string | null;
  /** ISO — last time admin sent digital book for user review. */
  digitalBookSentForReviewAt?: string | null;
  /** ISO string — last time admin published the digital book page invite. */
  digitalBookPagePublishedAt?: string | null;
  frontCover?: CoverData;
  backCover?: CoverData;
  pageImages?: PageImage[];
  generatedStory?: string;
}

const StoryResult: React.FC = () => {
  const { t } = useTranslation();
  const { uiLocale } = useUiLocale();
  const { sessionId } = useWizard();
  const { user, getAuthHeader, token } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const effectiveSessionId = useMemo(() => {
    const querySessionId = new URLSearchParams(location.search).get("sessionId");
    return querySessionId || sessionId || "";
  }, [location.search, sessionId]);

  const [storyData, setStoryData] = useState<StoryData | null>(null);
  const [pageSequence, setPageSequence] = useState<BookPage[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  /** False until page URLs are resolved and every book image has been fetched in the browser. */
  const [bookDisplayReady, setBookDisplayReady] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [requestText, setRequestText] = useState("");
  const [decisionError, setDecisionError] = useState<string | null>(null);
  const [decisionMessage, setDecisionMessage] = useState<string | null>(null);
  const [isSubmittingRequest, setIsSubmittingRequest] = useState(false);
  const [isApproving, setIsApproving] = useState(false);
  const [isSendingDigitalBook, setIsSendingDigitalBook] = useState(false);
  const [digitalBookSentLocked, setDigitalBookSentLocked] = useState(false);
  const [isSendingStoryImages, setIsSendingStoryImages] = useState(false);
  const [isPublishingDigitalBookPage, setIsPublishingDigitalBookPage] = useState(false);
  /** naturalWidth/naturalHeight; used to landscape-fit the StoryResult hero frame */
  const [heroCoverNaturalAspect, setHeroCoverNaturalAspect] = useState<number | null>(null);

  const isCompactViewport = useMediaQuery("(max-width: 640px)");

  // Fetch story data
  useEffect(() => {
    if (!effectiveSessionId) {
      navigate("/create/language");
      return;
    }

    if (user?.isAdmin === true) {
      fetch(`${API_BASE_URL}/admin/submissions/${encodeURIComponent(effectiveSessionId)}/progress`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getAuthHeader() },
        body: JSON.stringify({ adminLastStep: 'result' }),
      }).catch(() => {});
    }

    const fetchStoryData = async () => {
      try {
        setIsLoading(true);
        const isProductionRegularUser = user?.productionMode === true && user?.isAdmin !== true;
        if (isProductionRegularUser) {
          const requestResponse = await fetch(`${API_BASE_URL}/stories/book-request`, {
            headers: { ...getAuthHeader() },
          });
          const requestPayload = await requestResponse.json().catch(() => null);
          const requestData = requestPayload?.data;
          const requestStatus = requestData?.submissionStatus;
          const allowedStatuses = ["Your Story Is Ready", "Required Changes Submitted"];
          const canAccessStory =
            requestResponse.ok &&
            requestPayload?.success === true &&
            requestData?.sessionId === effectiveSessionId &&
            allowedStatuses.includes(requestStatus);
          if (!canAccessStory) {
            navigate("/create/order-summary", { replace: true });
            return;
          }
        }

        const response = await fetch(`${API_BASE_URL}/stories/${effectiveSessionId}`, {
          headers: { ...getAuthHeader() },
        });
        
        if (!response.ok) {
          throw new Error('Failed to load story data');
        }

        const data = await response.json();
        const story = data.data;

        if (!story) {
          throw new Error('Story not found');
        }

        setStoryData({
          sessionId: story.sessionId,
          language: story.language,
          status: story.status,
          submissionStatus: story.submissionStatus,
          userChangeRequestText: story.userChangeRequestText || "",
          userChangeRequestedAt: story.userChangeRequestedAt || null,
          userApprovedAt: story.userApprovedAt || null,
          storyImagesLastSentAt:
            typeof story.storyImagesLastSentAt === "string"
              ? story.storyImagesLastSentAt
              : story.storyImagesLastSentAt
                ? new Date(story.storyImagesLastSentAt).toISOString()
                : null,
          digitalBookSentForReviewAt:
            typeof story.digitalBookSentForReviewAt === "string"
              ? story.digitalBookSentForReviewAt
              : story.digitalBookSentForReviewAt
                ? new Date(story.digitalBookSentForReviewAt).toISOString()
                : null,
          digitalBookPagePublishedAt:
            typeof story.digitalBookPagePublishedAt === "string"
              ? story.digitalBookPagePublishedAt
              : story.digitalBookPagePublishedAt
                ? new Date(story.digitalBookPagePublishedAt).toISOString()
                : null,
          frontCover: story.frontCover,
          backCover: story.backCover,
          pageImages: story.pageImages || [],
          generatedStory: story.generatedStory
        });
      } catch (err: any) {
        console.error('StoryResult: failed to fetch story data', err instanceof Error ? err.message : err);
        setError(err.message || 'Failed to load story');
      } finally {
        setIsLoading(false);
      }
    };

    fetchStoryData();
  }, [effectiveSessionId, navigate, API_BASE_URL, user, getAuthHeader]);

  // Build page sequence
  useEffect(() => {
    if (!storyData) return;

    const buildPageSequence = async () => {
      setBookDisplayReady(false);
      try {
      const pages: BookPage[] = [];

      const resolveImageUrl = async (
        image: CoverData | PageImage | undefined,
        requireEdited: boolean = false
      ): Promise<string | null> => {
        if (!image) return null;
        if (requireEdited && !image.editedImageUrl && !image.editedImageStorageKey) {
          return null;
        }
        return resolveStoryImageUrl(image, token);
      };

      const resolveCover = async (cover: CoverData | undefined) => resolveCoverImageUrl(cover, token);

      // 1. Front cover (single page - closed book)
      const frontCoverUrl = await resolveCover(storyData.frontCover);
      pages.push({ 
        type: 'cover-front', 
        imageUrl: frontCoverUrl || undefined,
        isSinglePage: true,
        accessibleDescription: storyData.frontCover?.accessibleDescription
      });

      // 2. Two blank pages (spread when book opens) - MUST appear before story pages
      pages.push({ type: 'blank' });
      pages.push({ type: 'blank' });

      // 3. Story spreads: sequence [main, side] per page (digital reader lays them out side by side)
      if (storyData.pageImages && storyData.pageImages.length > 0) {
        const imgs = storyData.pageImages;
        const pageNums = imgs
          .map((img) => coerceStoryPageNumber(img.pageNumber))
          .filter((n): n is number => n != null);
        const maxPageNum = pageNums.length > 0 ? Math.max(...pageNums) : 0;

        for (let pageNum = 1; pageNum <= maxPageNum; pageNum++) {
          const mainImg = findStoryPageSlotImage(imgs, pageNum, false);
          const sideImg = findStoryPageSlotImage(imgs, pageNum, true);
          const mainResolved = await resolveImageUrl(mainImg, false);
          const sideResolved = await resolveImageUrl(sideImg, false);
          const mainAlt =
            (typeof mainImg?.accessibleDescription === 'string' ? mainImg.accessibleDescription : '').trim();
          const sideAlt =
            (typeof sideImg?.accessibleDescription === 'string' ? sideImg.accessibleDescription : '').trim();

          if (mainResolved && sideResolved) {
            pages.push({
              type: 'story',
              pageNumber: pageNum,
              imageUrl: mainResolved,
              isFullSpread: false,
              accessibleDescription: mainAlt || sideAlt,
            });
            pages.push({
              type: 'story',
              pageNumber: pageNum,
              imageUrl: sideResolved,
              isFullSpread: false,
              accessibleDescription: sideAlt || mainAlt,
            });
          } else if (mainResolved || sideResolved) {
            const u = (mainResolved || sideResolved) as string;
            const alt = (mainResolved ? mainAlt : sideAlt) || mainAlt || sideAlt;
            pages.push({
              type: 'story',
              pageNumber: pageNum,
              imageUrl: u,
              isFullSpread: false,
              accessibleDescription: alt,
            });
            pages.push({
              type: 'story',
              pageNumber: pageNum,
              imageUrl: u,
              isFullSpread: false,
              accessibleDescription: alt,
            });
          }
        }
      }

      // 4. Back cover (single page - closed book)
      const backCoverUrl = await resolveCover(storyData.backCover);
      pages.push({ 
        type: 'cover-back', 
        imageUrl: backCoverUrl || undefined,
        isSinglePage: true,
        accessibleDescription: storyData.backCover?.accessibleDescription
      });

      setPageSequence(pages);

      const uniqueImageUrls = [...new Set(pages.map((p) => p.imageUrl).filter(Boolean))] as string[];
      await Promise.all(
        uniqueImageUrls.map(
          (src) =>
            new Promise<void>((resolve) => {
              const img = new Image();
              const done = () => resolve();
              img.onload = done;
              img.onerror = done;
              img.src = src;
              if (img.complete && img.naturalWidth > 0) {
                done();
              }
            })
        )
      );
      } catch (err) {
        console.error("StoryResult: buildPageSequence failed", err);
      } finally {
        setBookDisplayReady(true);
      }
    };

    buildPageSequence();
  }, [storyData, token]);

  const storyImagesSentAtLabel = useMemo(() => {
    const raw = storyData?.storyImagesLastSentAt;
    if (!raw) return null;
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleString(uiLocale === "he" ? "he-IL" : "en-US", {
      dateStyle: "medium",
      timeStyle: "short",
    });
  }, [storyData?.storyImagesLastSentAt, uiLocale]);

  const digitalBookSentForReviewAtLabel = useMemo(() => {
    const raw = storyData?.digitalBookSentForReviewAt;
    if (!raw) return null;
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleString(uiLocale === "he" ? "he-IL" : "en-US", {
      dateStyle: "medium",
      timeStyle: "short",
    });
  }, [storyData?.digitalBookSentForReviewAt, uiLocale]);

  const digitalBookPagePublishedAtLabel = useMemo(() => {
    const raw = storyData?.digitalBookPagePublishedAt;
    if (!raw) return null;
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleString(uiLocale === "he" ? "he-IL" : "en-US", {
      dateStyle: "medium",
      timeStyle: "short",
    });
  }, [storyData?.digitalBookPagePublishedAt, uiLocale]);

  const heroCoverUrl = pageSequence[0]?.imageUrl;

  useEffect(() => {
    setHeroCoverNaturalAspect(null);
  }, [heroCoverUrl]);

  /** Decode intrinsic size ASAP (cached imgs may populate before hero <img> onLoad). */
  useEffect(() => {
    if (!heroCoverUrl) return;
    const im = new Image();
    const apply = () => {
      const { naturalWidth: nw, naturalHeight: nh } = im;
      if (nw > 0 && nh > 0) setHeroCoverNaturalAspect(nw / nh);
    };
    im.onload = apply;
    im.onerror = apply;
    im.src = heroCoverUrl;
    if (im.complete && im.naturalWidth > 0) apply();
  }, [heroCoverUrl]);

  /** Hero frame follows cover aspect (landscape = wide frame, portrait = tall). */
  const { coverW, coverH } = useMemo(() => {
    const maxW = isCompactViewport ? 360 : 560;
    const maxH = isCompactViewport ? 380 : 520;
    const placeholderAr = 3 / 4;
    if (!heroCoverUrl) {
      const w = isCompactViewport ? 220 : 320;
      const h = isCompactViewport ? 290 : 420;
      return { coverW: w, coverH: h };
    }
    const raw = heroCoverNaturalAspect;
    const ar =
      raw != null && raw > 0 && Number.isFinite(raw) ? raw : placeholderAr;
    let w = maxW;
    let h = w / ar;
    if (h > maxH) {
      h = maxH;
      w = h * ar;
    }
    return { coverW: Math.round(Math.max(w, 120)), coverH: Math.round(Math.max(h, 120)) };
  }, [heroCoverUrl, heroCoverNaturalAspect, isCompactViewport]);

  const sentTimestampCard = (titleKey: string, dateLabel: string) => (
    <p
      className="max-w-[220px] rounded-xl border border-brandNavy/15 bg-white/95 px-3 py-2 text-center text-sm text-brandNavy sm:max-w-[280px]"
      dir={uiLocale === "he" ? "rtl" : "ltr"}
    >
      <span className="block text-xs font-medium text-gray-600">{t(titleKey)}</span>
      <span className="font-semibold leading-snug">{dateLabel}</span>
    </p>
  );

  if (isLoading || (storyData && !bookDisplayReady)) {
    return (
      <div className="relative w-full min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-amber-100 flex flex-col items-center justify-center px-4">
        <main id="main-content" tabIndex={-1} className="flex flex-col items-center outline-none" aria-busy="true">
          <LogoLoader size="lg" />
          <p className="mt-4 max-w-md text-center text-base font-semibold text-brandNavy">
            {isLoading ? t("wizard.storyResult.loadingStory") : t("wizard.storyResult.loadingBookImages")}
          </p>
        </main>
      </div>
    );
  }

  if (error || !storyData) {
    return (
      <div className="relative w-full min-h-screen bg-cover bg-center bg-no-repeat flex flex-col items-center justify-center px-4">
        <main id="main-content" tabIndex={-1} className="max-w-2xl w-full outline-none">
          <div className="bg-white/95 backdrop-blur-md rounded-2xl p-8 shadow-card border border-white/40 w-full text-center">
            <h1 className="text-3xl font-extrabold text-heading mb-4">{t("wizard.storyResult.errorTitle")}</h1>
            <AdminPageNavigator align="center" />
            <p className="text-red-700 mb-6" role="alert">
              {error || t("wizard.storyResult.storyNotFound")}
            </p>
            <button
              type="button"
              onClick={() => navigate("/create/language")}
              className="bg-brandOrange hover:bg-orange-600 text-white font-semibold px-6 py-3 rounded-2xl shadow-card hover:shadow-glow transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-brandOrange focus:ring-offset-2"
            >
              {t("wizard.storyResult.goBack")}
            </button>
          </div>
        </main>
      </div>
    );
  }

  const isAdmin = user?.isAdmin === true;
  const canUserReview =
    !isAdmin &&
    (storyData.submissionStatus === "Your Story Is Ready" ||
      storyData.submissionStatus === "Required Changes Submitted");
  const canSubmitChangeRequest = storyData.submissionStatus === "Your Story Is Ready";
  const isUserApproved = !isAdmin && (storyData.status === "completed" || !!storyData.userApprovedAt);

  const heroTitle = coverDisplayTitleFromOverlays(
    storyData.frontCover ? (storyData.frontCover as { textOverlays?: unknown }) : undefined
  );
  const readDigitalHref = `/create/digital-book?sessionId=${encodeURIComponent(effectiveSessionId)}&p=1`;

  const handleApproveDigitalBook = async () => {
    if (!effectiveSessionId || isApproving) return;
    setDecisionError(null);
    setDecisionMessage(null);
    setIsApproving(true);
    try {
      const response = await fetch(`${API_BASE_URL}/stories/book-request/${encodeURIComponent(effectiveSessionId)}/approve`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...getAuthHeader() },
      });
      const payload = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(payload?.message || "Failed to approve digital book");
      }
      setStoryData((prev) => (prev ? {
        ...prev,
        status: payload?.data?.status || "completed",
        userApprovedAt: payload?.data?.userApprovedAt || new Date().toISOString(),
      } : prev));
      setDecisionMessage(t("wizard.storyResult.approveSuccess"));
    } catch (err: any) {
      setDecisionError(err?.message || t("wizard.storyResult.approveFailed"));
    } finally {
      setIsApproving(false);
    }
  };

  const handleSubmitChangeRequest = async () => {
    if (!effectiveSessionId || isSubmittingRequest) return;
    if (!requestText.trim()) {
      setDecisionError(t("wizard.storyResult.describeChangesRequired"));
      return;
    }
    setDecisionError(null);
    setDecisionMessage(null);
    setIsSubmittingRequest(true);
    try {
      const response = await fetch(`${API_BASE_URL}/stories/book-request/${encodeURIComponent(effectiveSessionId)}/change-request`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...getAuthHeader() },
        body: JSON.stringify({ changeRequestText: requestText.trim() }),
      });
      const payload = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(payload?.message || "Failed to submit change request");
      }
      setStoryData((prev) => (prev ? {
        ...prev,
        submissionStatus: payload?.data?.submissionStatus || "Required Changes Submitted",
        userChangeRequestText: payload?.data?.userChangeRequestText || requestText.trim(),
        userChangeRequestedAt: payload?.data?.userChangeRequestedAt || new Date().toISOString(),
      } : prev));
      setDecisionMessage(t("wizard.storyResult.changeRequestSuccess"));
      setRequestText("");
    } catch (err: any) {
      setDecisionError(err?.message || t("wizard.storyResult.changeRequestFailed"));
    } finally {
      setIsSubmittingRequest(false);
    }
  };

  const handleSendDigitalBook = async () => {
    if (!effectiveSessionId || isSendingDigitalBook || digitalBookSentLocked) return;
    setDecisionError(null);
    setDecisionMessage(null);
    setIsSendingDigitalBook(true);
    try {
      const response = await fetch(
        `${API_BASE_URL}/admin/submissions/${encodeURIComponent(effectiveSessionId)}/send-digital-book`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json", ...getAuthHeader() },
        }
      );
      const payload = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(payload?.message || "Failed to send digital book");
      }
      const nextSubmissionStatus = payload?.data?.submissionStatus || "Your Story Is Ready";
      const reviewSentAt =
        typeof payload?.data?.digitalBookSentForReviewAt === "string"
          ? payload.data.digitalBookSentForReviewAt
          : payload?.data?.digitalBookSentForReviewAt != null
            ? new Date(payload.data.digitalBookSentForReviewAt).toISOString()
            : new Date().toISOString();
      setStoryData((prev) =>
        prev
          ? {
              ...prev,
              submissionStatus: nextSubmissionStatus,
              digitalBookSentForReviewAt: reviewSentAt,
            }
          : prev
      );
      setDigitalBookSentLocked(true);
      setDecisionMessage(t("wizard.storyResult.sendSuccess"));
    } catch (err: any) {
      setDecisionError(err?.message || t("wizard.storyResult.sendFailed"));
    } finally {
      setIsSendingDigitalBook(false);
    }
  };

  const handleSendStoryImages = async () => {
    if (!effectiveSessionId || isSendingStoryImages) return;
    setDecisionError(null);
    setDecisionMessage(null);
    setIsSendingStoryImages(true);
    try {
      const response = await fetch(
        `${API_BASE_URL}/admin/submissions/${encodeURIComponent(effectiveSessionId)}/send-story-images`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json", ...getAuthHeader() },
        }
      );
      const payload = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(payload?.message || "Failed to send story images");
      }
      const sentAt =
        typeof payload?.data?.storyImagesLastSentAt === "string"
          ? payload.data.storyImagesLastSentAt
          : null;
      setStoryData((prev) =>
        prev
          ? {
              ...prev,
              storyImagesLastSentAt: sentAt ?? prev.storyImagesLastSentAt ?? null,
            }
          : prev
      );
      setDecisionMessage(t("wizard.storyResult.sendStoryImagesSuccess"));
    } catch (err: any) {
      setDecisionError(err?.message || t("wizard.storyResult.sendStoryImagesFailed"));
    } finally {
      setIsSendingStoryImages(false);
    }
  };

  const handlePublishDigitalBookPage = async () => {
    if (!effectiveSessionId || isPublishingDigitalBookPage) return;
    setDecisionError(null);
    setDecisionMessage(null);
    setIsPublishingDigitalBookPage(true);
    try {
      const response = await fetch(
        `${API_BASE_URL}/admin/submissions/${encodeURIComponent(effectiveSessionId)}/publish-digital-book-page`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json", ...getAuthHeader() },
        }
      );
      const payload = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(payload?.message || t("wizard.storyResult.publishDigitalBookPageFailed"));
      }
      const publishedAt =
        typeof payload?.data?.digitalBookPagePublishedAt === "string"
          ? payload.data.digitalBookPagePublishedAt
          : payload?.data?.digitalBookPagePublishedAt != null
            ? new Date(payload.data.digitalBookPagePublishedAt).toISOString()
            : new Date().toISOString();
      setStoryData((prev) =>
        prev ? { ...prev, digitalBookPagePublishedAt: publishedAt } : prev
      );
      setDecisionMessage(t("wizard.storyResult.publishDigitalBookPageSuccess"));
    } catch (err: any) {
      setDecisionError(err?.message || t("wizard.storyResult.publishDigitalBookPageFailed"));
    } finally {
      setIsPublishingDigitalBookPage(false);
    }
  };

  return (
    <div className="relative flex min-h-screen w-full flex-col bg-gradient-to-br from-amber-50 via-orange-50 to-amber-100">
      <main
        id="main-content"
        tabIndex={-1}
        className="relative mx-auto flex w-full max-w-6xl flex-1 flex-col items-center px-4 pt-10 pb-12 outline-none sm:pb-16"
      >
        <AdminPageNavigator align="center" />

        <section className="relative z-[1] flex w-full flex-col items-center pb-6 sm:pb-12" aria-labelledby="result-hero-heading">
          <div
            style={{
              width: coverW,
              height: coverH,
              borderRadius: 14,
              perspective: 800,
            }}
          >
            <div
              className="relative flex h-full w-full items-center justify-center overflow-hidden"
              style={{
                borderRadius: 14,
                background: heroCoverUrl ? "#f8f8f8" : "linear-gradient(135deg,#f6c89c,#E07A52)",
                transform: "perspective(800px) rotateY(-6deg)",
                boxShadow: "0 16px 40px rgba(44,62,80,0.30), 0 4px 12px rgba(0,0,0,0.15)",
              }}
              aria-labelledby="result-hero-heading"
            >
              <h1 id="result-hero-heading" className="sr-only">
                {(heroTitle && heroTitle.trim()) || t("wizard.storyResult.coverEyebrow")}
              </h1>
              <div
                className="pointer-events-none absolute left-0 top-0 bottom-0 z-[2]"
                style={{
                  width: 6,
                  background: "linear-gradient(90deg, rgba(0,0,0,0.25), transparent)",
                }}
              />

              {heroCoverUrl ? (
                <img
                  src={heroCoverUrl}
                  alt={storyData.frontCover?.accessibleDescription?.trim() || ""}
                  className="relative z-[1] max-h-full max-w-full object-contain object-center"
                  onLoad={(e) => {
                    const { naturalWidth: nw, naturalHeight: nh } = e.currentTarget;
                    if (nw > 0 && nh > 0) setHeroCoverNaturalAspect(nw / nh);
                  }}
                />
              ) : null}
            </div>
          </div>
        </section>

        <Link
          to={readDigitalHref}
          className="mt-4 flex w-full max-w-lg min-h-[52px] items-center justify-center rounded-2xl bg-brandOrange px-4 text-base font-bold text-white shadow-soft transition-colors hover:bg-[#d96840]"
        >
          {t("wizard.storyResult.readDigitalBookCta")}
        </Link>

        {isAdmin ? (
          <section
            className="mt-6 w-full max-w-lg rounded-2xl border border-brandNavy/15 bg-white/95 px-6 py-6 backdrop-blur-sm"
            aria-label={t("wizard.storyResult.coverEyebrow")}
          >
            <div className="flex w-full flex-col gap-6 sm:flex-row sm:flex-wrap sm:justify-between">
              <div className="flex min-w-0 flex-1 flex-col items-stretch gap-2">
                <button
                  type="button"
                  onClick={handleSendDigitalBook}
                  disabled={isSendingDigitalBook || digitalBookSentLocked}
                  className={`w-full rounded-xl px-4 py-2.5 text-sm font-bold text-white disabled:cursor-not-allowed sm:max-w-xs ${
                    digitalBookSentLocked
                      ? "bg-gray-400"
                      : "bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50"
                  }`}
                >
                  {isSendingDigitalBook ? "…" : t("wizard.storyResult.sendDigitalBook")}
                </button>
                {digitalBookSentForReviewAtLabel
                  ? sentTimestampCard(
                      "wizard.storyResult.sentDigitalBookForReviewIn",
                      digitalBookSentForReviewAtLabel
                    )
                  : null}
              </div>
              <div className="flex min-w-0 flex-1 flex-col items-stretch gap-2">
                <button
                  type="button"
                  onClick={handleSendStoryImages}
                  disabled={isSendingStoryImages}
                  className="w-full max-w-none rounded-xl bg-[#1E90FF] px-4 py-2.5 text-sm font-bold text-white hover:bg-[#1872cc] disabled:cursor-not-allowed disabled:opacity-50 sm:max-w-xs"
                >
                  {isSendingStoryImages ? "…" : t("wizard.storyResult.sendStoryImages")}
                </button>
                {storyImagesSentAtLabel
                  ? sentTimestampCard("wizard.storyResult.sentStoryImagesIn", storyImagesSentAtLabel)
                  : null}
              </div>
              <div className="flex min-w-0 flex-1 flex-col items-stretch gap-2">
                <button
                  type="button"
                  onClick={handlePublishDigitalBookPage}
                  disabled={isPublishingDigitalBookPage}
                  className="w-full max-w-none rounded-xl bg-brandOrange px-4 py-2.5 text-sm font-bold text-white hover:bg-orange-600 disabled:cursor-not-allowed disabled:opacity-50 sm:max-w-xs"
                >
                  {isPublishingDigitalBookPage ? "…" : t("wizard.storyResult.publishDigitalBookPage")}
                </button>
                {digitalBookPagePublishedAtLabel
                  ? sentTimestampCard(
                      "wizard.storyResult.sentDigitalBookPageIn",
                      digitalBookPagePublishedAtLabel
                    )
                  : null}
              </div>
            </div>
          </section>
        ) : null}

        {!canUserReview && decisionMessage ? (
          <p className="mb-6 text-center text-sm font-semibold text-green-700">{decisionMessage}</p>
        ) : null}
        {!canUserReview && decisionError ? (
          <p className="mb-4 text-center text-sm text-red-700">{decisionError}</p>
        ) : null}

        {isAdmin && storyData.userChangeRequestText && storyData.userChangeRequestText.trim().length > 0 && (
          <div className="mt-6 w-full max-w-4xl rounded-2xl border border-red-200 bg-red-50/90 p-4">
            <p className="text-sm font-semibold text-red-900">Submitted change request</p>
            <p className="mt-2 whitespace-pre-wrap text-sm text-red-900">{storyData.userChangeRequestText}</p>
          </div>
        )}

      {canUserReview && (
        <div className="mt-6 w-full max-w-4xl rounded-2xl border border-white/50 bg-white/90 p-4 shadow-soft">
          {!isUserApproved && <p className="text-base font-semibold text-heading">{t("wizard.storyResult.reviewTitle")}</p>}
          <p className="mt-1 text-sm text-body">
            {isUserApproved
              ? t("wizard.storyResult.reviewApprovedMessage")
              : t("wizard.storyResult.reviewPrompt")}
          </p>
          {!isUserApproved && (
            <textarea
              value={requestText}
              onChange={(e) => setRequestText(e.target.value)}
              placeholder={
                canSubmitChangeRequest
                  ? t("wizard.storyResult.changeRequestPlaceholder")
                  : t("wizard.storyResult.changeRequestLockedPlaceholder")
              }
              rows={4}
              disabled={!canSubmitChangeRequest}
              className="mt-3 w-full rounded-xl border border-gray-300 p-3 text-sm text-heading focus:outline-none focus:ring-2 focus:ring-brandOrange disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
            />
          )}
          {decisionError && <p className="mt-2 text-sm text-red-700">{decisionError}</p>}
          {decisionMessage && <p className="mt-2 text-sm text-green-700">{decisionMessage}</p>}
          {!isUserApproved && (
            <div className="mt-3 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={handleApproveDigitalBook}
                disabled={isApproving || isSubmittingRequest}
                className="rounded-xl bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isApproving ? t("wizard.storyResult.approving") : t("wizard.storyResult.approve")}
              </button>
              <button
                type="button"
                onClick={handleSubmitChangeRequest}
                disabled={!canSubmitChangeRequest || isApproving || isSubmittingRequest}
                className="rounded-xl bg-brandOrange px-4 py-2 text-sm font-semibold text-white hover:bg-orange-600 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isSubmittingRequest ? t("wizard.storyResult.submitting") : t("wizard.storyResult.submitChangeRequest")}
              </button>
            </div>
          )}
        </div>
      )}

        <div className="mt-auto flex w-full justify-start pt-10 pb-6">
          <button
            type="button"
            onClick={() => navigate(isAdmin ? "/create/cover-studio" : "/create/order-summary")}
            className={`rounded-2xl border-2 border-brandNavy/30 bg-white/90 px-6 py-2 text-heading transition-all focus:outline-none focus:ring-2 focus:ring-brandOrange focus:ring-offset-2 ${
              isAdmin ? "hover:bg-white" : "shadow-soft hover:bg-white hover:shadow-card"
            }`}
          >
            {isAdmin ? `← ${t("wizard.storyResult.backToCoverStudio")}` : `← ${t("wizard.storyResult.backToRequestSummary")}`}
          </button>
        </div>
      </main>
    </div>
  );
};

export default StoryResult;
